import { IBusinessInfo, IPlan, ISearchedProfile } from "../types";

export const GEORANKING_ONBOARDING_STEPS = [
  "Validação",
  "Dados",
  "Perfil",
  "Categoria",
  "Palavras-chave",
  "Descrição",
  "Plano",
  "Pagamento",
] as const;

export type GeoRankingOnboardingStep =
  (typeof GEORANKING_ONBOARDING_STEPS)[number];

export const LEGAL_DOCUMENT_LINKS = [
  {
    label: "Termos de Uso",
    href: "https://georanking.com.br/termos-de-uso/",
  },
  {
    label: "Política de Privacidade",
    href: "https://georanking.com.br/politica-de-privacidade/",
  },
] as const;

export const VALIDATION_CHECKPOINTS = [
  "Conferir se o negócio e o responsável batem com a contratação",
  "Preparar os dados para SEO local sem publicar nada automaticamente",
  "Enviar as informações para revisão interna antes de qualquer ação",
] as const;

export const VALIDATION_TRUST_SIGNALS = [
  "Ambiente GeoRanking",
  "Conferência humana",
  "Nada publicado sem aprovação",
] as const;

export const createEmptyBusinessInfo = (): IBusinessInfo => ({
  displayName: "",
  razaoSocial: "",
  cpfCnpj: "",
  enderecoComercial: {
    rua: "",
    bairro: "",
    cidade: "",
    estado: "",
    complemento: "",
  },
  telefone: "",
  email: "",
  legalRepresentative: {
    name: "",
    phoneNumber: "",
    email: "",
  },
  accountManager: {
    name: "",
    phoneNumber: "",
    email: "",
  },
});

export const isBusinessInfoComplete = (businessInfo: IBusinessInfo) =>
  businessInfo.razaoSocial.trim() !== "" &&
  businessInfo.cpfCnpj.trim() !== "" &&
  businessInfo.telefone.trim() !== "" &&
  businessInfo.email.trim() !== "" &&
  businessInfo.enderecoComercial.rua.trim() !== "" &&
  businessInfo.enderecoComercial.bairro.trim() !== "" &&
  businessInfo.enderecoComercial.cidade.trim() !== "" &&
  businessInfo.enderecoComercial.estado.trim() !== "";

export const isValidationStepComplete = ({
  acceptedTerms,
  hasOAuthToken,
}: {
  acceptedTerms: boolean;
  hasOAuthToken: boolean;
}) => acceptedTerms && hasOAuthToken;

export const shouldShowOAuthLoginFirst = (hasOAuthToken: boolean) =>
  !hasOAuthToken;

export const getStepSupportText = (step: number) => {
  switch (GEORANKING_ONBOARDING_STEPS[step]) {
    case "Validação":
      return "Conecte sua conta Google e aceite os documentos para liberar a busca do perfil real.";
    case "Dados":
      return "Informe apenas o essencial para contrato, contato e localização.";
    case "Perfil":
      return "Escolha o perfil do Google que vamos acompanhar. Se você não tem acesso, pesquise pelo nome público.";
    case "Categoria":
      return "A categoria diz ao Google qual é o principal serviço da empresa.";
    case "Palavras-chave":
      return "Escolha buscas reais que seus clientes fariam para comprar ou contratar.";
    case "Descrição":
      return "Revise o texto que explica o negócio de forma clara para clientes e para o Google.";
    case "Plano":
      return "Escolha o alcance e a quantidade de ações mais adequada para o momento da empresa.";
    case "Pagamento":
      return "Estamos preparando o link seguro para finalizar a contratação.";
    default:
      return "";
  }
};

const splitWords = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((word) => word.length > 2);

const getBusinessSegment = (businessInfo: IBusinessInfo) => {
  const source = `${businessInfo.razaoSocial} ${businessInfo.extraDescription || ""}`;
  const words = splitWords(source);
  if (words.includes("borracharia") || words.includes("pneu")) {
    return "borracharia";
  }
  if (words.includes("bar") || words.includes("restaurante")) {
    return "bar e restaurante";
  }
  if (words.includes("assistencia") || words.includes("celular")) {
    return "assistência técnica";
  }
  if (words.includes("mecanica") || words.includes("oficina")) {
    return "oficina mecânica";
  }
  return businessInfo.razaoSocial || "empresa local";
};

export const createManualProfileFromSearch = (
  query: string,
  businessInfo: IBusinessInfo,
): ISearchedProfile => {
  const cityState = [
    businessInfo.enderecoComercial.cidade,
    businessInfo.enderecoComercial.estado,
  ]
    .filter(Boolean)
    .join(" - ");

  return {
    displayName: query.trim() || businessInfo.razaoSocial || "Meu negócio",
    formattedAddress: [
      businessInfo.enderecoComercial.rua,
      businessInfo.enderecoComercial.bairro,
      cityState,
    ]
      .filter(Boolean)
      .join(", "),
    types: ["manual_profile"],
    briefing: businessInfo.extraDescription || "",
    keywords: [],
    profileId: `manual-${Date.now()}`,
    isManual: true,
  };
};

export const buildFallbackCategories = (businessInfo: IBusinessInfo) => {
  const segment = getBusinessSegment(businessInfo);
  const categoriesBySegment: Record<string, string[]> = {
    borracharia: [
      "Borracharia",
      "Loja de pneus",
      "Serviço de alinhamento e balanceamento",
      "Oficina mecânica",
    ],
    "bar e restaurante": [
      "Bar",
      "Restaurante",
      "Lanchonete",
      "Restaurante familiar",
    ],
    "assistência técnica": [
      "Assistência técnica",
      "Loja de reparo de celulares",
      "Serviço de conserto de computadores",
      "Loja de eletrônicos",
    ],
    "oficina mecânica": [
      "Oficina mecânica",
      "Auto center",
      "Serviço de troca de óleo",
      "Mecânico",
    ],
  };

  return (
    categoriesBySegment[segment] || [
      "Prestador de serviços",
      "Empresa local",
      "Loja",
      "Serviço profissional",
    ]
  );
};

export const buildFallbackKeywords = (
  businessInfo: IBusinessInfo,
  categories: string[],
) => {
  const city = businessInfo.enderecoComercial.cidade || "minha cidade";
  const segment = getBusinessSegment(businessInfo);
  const base = categories[0] || segment;

  return [
    `${base} em ${city}`,
    `${base} perto de mim em ${city}`,
    `onde contratar ${base} em ${city}`,
    `${segment} com orçamento em ${city}`,
    `${segment} aberto agora em ${city}`,
    `${segment} confiável em ${city}`,
  ];
};

export const buildFallbackBriefing = (
  businessInfo: IBusinessInfo,
  categories: string[],
  keywords: string[],
) => {
  const city = businessInfo.enderecoComercial.cidade || "sua região";
  const segment = getBusinessSegment(businessInfo);
  const categoryText = categories[0] || segment;
  const keywordText = keywords.slice(0, 3).join(", ");

  return `${businessInfo.razaoSocial || "A empresa"} atua em ${city} com foco em ${categoryText}. O objetivo da campanha é melhorar a presença local no Google para buscas com intenção de compra, como ${keywordText}. ${businessInfo.extraDescription || "A comunicação deve ser simples, confiável e direcionada para clientes próximos que precisam contratar agora."}`;
};

export const fallbackPlans: IPlan[] = [
  {
    id: 901,
    name: "Bronze",
    maxKeywords: 2,
    visitCard: true,
    qrCode: true,
    monthlyCost: 210,
    externalId: "price_1T2d8sB9zvD0uiNfzEHmss9E",
    indDeleted: false,
  },
  {
    id: 902,
    name: "Prata",
    maxKeywords: 4,
    visitCard: true,
    qrCode: true,
    monthlyCost: 294,
    externalId: "price_1T2dClB9zvD0uiNfCX38NBKD",
    indDeleted: false,
  },
  {
    id: 903,
    name: "Ouro",
    maxKeywords: 6,
    visitCard: true,
    qrCode: true,
    monthlyCost: 374,
    externalId: "price_1T2dFDB9zvD0uiNf5NKK5ebr",
    indDeleted: false,
  },
  {
    id: 904,
    name: "Diamante",
    maxKeywords: 8,
    visitCard: true,
    qrCode: true,
    monthlyCost: 499,
    externalId: "price_1T2dGUB9zvD0uiNf3GT1K1tO",
    indDeleted: false,
  },
];
